import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  Iskelet,
  Kart,
  MetinAlani,
  OnayKutusu,
  Rozet,
  SatirDugmesi,
  SatirMetni,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  sablonArsivle,
  sablonKaydet,
  sablonListesi,
  sablonOnayaGonder,
  sablonTestGonder,
  type SablonOlayi,
  type SablonSatiri,
} from "./api";
import { useSudoKapisi } from "./SudoKapisi";

/**
 * BILDIRIM SABLONLARI — F-TEC-10
 *
 * "Metin duzenleme, degisken listesi, onizleme, test gonderimi
 *  (yalnizca kendi hesabina), WhatsApp sablon onay durumu takibi."
 */

type Kanal = "EMAIL" | "SMS" | "WHATSAPP" | "PUSH" | "IN_APP";

/*
  ================================================================
  ÇALIŞMAYAN KANALLAR DÜZENLENMEZ — urun-mutabakat H5 (05.10.2026)
  ================================================================
  SMS, WhatsApp ve anlık bildirim için sağlayıcı seçilmedi (§9.16) ve
  Emre'nin kararı ilk dış kanalın YALNIZ e-posta olması. O kanallarda
  şablon yazdırmak "ayarladım ama gitmiyor" yanılgısı üretiyordu.
  Düzenleme gizleniyor, VERİ SİLİNMİYOR (kural 7): kayıtlı şablon varsa
  sayısı "karar bekliyor" satırında görünüyor. Sağlayıcı kararı gelince
  kanal bu listeye geri eklenir.
*/
const DUZENLENEBILIR_KANALLAR: readonly Kanal[] = ["EMAIL", "IN_APP"];
const KARAR_BEKLEYEN_KANALLAR: readonly Kanal[] = ["SMS", "WHATSAPP", "PUSH"];

const KANAL_ADI: Record<Kanal, string> = {
  EMAIL: "E-posta",
  SMS: "SMS",
  WHATSAPP: "WhatsApp",
  PUSH: "Anlık bildirim",
  IN_APP: "Panel içi",
};

const ONAY_ADI: Record<SablonSatiri["onayDurumu"], string> = {
  DRAFT: "Taslak",
  SUBMITTED: "Onay bekliyor",
  APPROVED: "Onaylı",
  REJECTED: "Reddedildi",
};

const ONAY_TONU: Record<SablonSatiri["onayDurumu"], "notr" | "uyari" | "onay" | "hata"> = {
  DRAFT: "notr",
  SUBMITTED: "uyari",
  APPROVED: "onay",
  REJECTED: "hata",
};

export function BildirimSablonlari({ arsivleyebilir }: { arsivleyebilir: boolean }) {
  const qc = useQueryClient();
  const [secilenOlay, setSecilenOlay] = useState<string | null>(null);

  const { hataYakala, kapi } = useSudoKapisi();

  const liste = useQuery<SablonOlayi[]>({
    queryKey: ["sablonlar"],
    queryFn: sablonListesi,
    retry: false,
  });

  if (liste.isPending) return <Iskelet satir={6} />;
  if (liste.isError) {
    return (
      <Kart ton="hata" baslik="Şablonlar yüklenemedi">
        <p className="text-notr-700 text-sm">
          {liste.error instanceof ApiHatasi ? liste.error.message : "Bilinmeyen hata."}
        </p>
      </Kart>
    );
  }

  const secilen = liste.data.find((o) => o.olay === secilenOlay) ?? null;

  return (
    <div className="flex flex-col gap-4">
      {kapi}

      {secilen ? (
        <OlayDuzenle
          olay={secilen}
          arsivleyebilir={arsivleyebilir}
          hataYakala={hataYakala}
          onGeri={() => setSecilenOlay(null)}
          onDegisti={() => void qc.invalidateQueries({ queryKey: ["sablonlar"] })}
        />
      ) : (
        <OlayListesi olaylar={liste.data} onSec={setSecilenOlay} />
      )}
    </div>
  );
}

// =====================================================================
// OLAY LİSTESİ
// =====================================================================

function OlayListesi({
  olaylar,
  onSec,
}: {
  olaylar: SablonOlayi[];
  onSec: (olay: string) => void;
}) {
  /**
   * ================================================================
   * ŞABLONU OLMAYAN OLAY DA LİSTEDE
   * ================================================================
   * Ekranın cevaplaması gereken asıl soru "hangi olayın şablonu
   * EKSİK". Yalnızca var olan şablonları listeleseydik, hiç şablonu
   * olmayan bir olay ekranda hiç görünmez ve kimse eksikliği fark
   * etmezdi — bildirimler sessizce panel içinde kalırdı.
   */
  const eksik = olaylar.filter((o) => o.sablonlar.length === 0);

  return (
    <>
      <Kart
        baslik="Bildirim şablonları"
        aciklama={`${olaylar.length} olay · ${eksik.length} tanesinin hiç şablonu yok`}
        ton={eksik.length > 0 ? "uyari" : "notr"}
      >
        <p className="text-notr-700 text-sm">
          Şablonu olmayan olaylarda bildirim yalnızca panel içinde kalır. Dış kanal bugün yalnız
          e-posta; hangi olayın e-postayla gidebileceği sistemde sabit (veliye yalnız rapor
          bağlantısı) — diğer olaylarda e-posta şablonu olsa da bildirim panel içinde kalır. SMS,
          WhatsApp ve anlık bildirim karar bekliyor. Şablon metninde{" "}
          <code className="text-xs">{"{{değişken}}"}</code> kullanabilirsiniz — kullanılabilir
          olanları olayı açınca görürsünüz.
        </p>
      </Kart>

      <ul className="flex flex-col gap-2">
        {olaylar.map((o) => (
          <li key={o.olay}>
            <SatirDugmesi onClick={() => onSec(o.olay)}>
              <SatirMetni
                baslik={o.olay}
                altBaslik={
                  o.sablonlar.length === 0
                    ? "Şablon yok"
                    : o.sablonlar.map((s) => KANAL_ADI[s.kanal as Kanal] ?? s.kanal).join(" · ")
                }
              />
              {o.sablonlar.length === 0 ? (
                <Rozet ton="uyari">eksik</Rozet>
              ) : (
                <Rozet ton="onay">{o.sablonlar.length}</Rozet>
              )}
            </SatirDugmesi>
          </li>
        ))}
      </ul>
    </>
  );
}

/** SMS/WhatsApp/anlık bildirim — tek satır; kayıtlı şablon varsa sayısı (silinmedi). */
function KararBekleyenKanallar({ olay }: { olay: SablonOlayi }) {
  const kayitli = olay.sablonlar.filter((s) => KARAR_BEKLEYEN_KANALLAR.includes(s.kanal as Kanal));
  return (
    <p className="text-notr-600 mt-3 text-sm">
      {KARAR_BEKLEYEN_KANALLAR.map((k) => KANAL_ADI[k]).join(", ")}: karar bekliyor — sağlayıcı
      seçilmedi.
      {kayitli.length > 0 &&
        ` Bu kanallarda kayıtlı ${kayitli.length} şablon korunuyor; düzenleme kapalı.`}
    </p>
  );
}

// =====================================================================
// OLAY DÜZENLEME
// =====================================================================

function OlayDuzenle({
  olay,
  arsivleyebilir,
  hataYakala,
  onGeri,
  onDegisti,
}: {
  olay: SablonOlayi;
  arsivleyebilir: boolean;
  hataYakala: (tekrarDene: () => void) => (e: unknown) => void;
  onGeri: () => void;
  onDegisti: () => void;
}) {
  const [kanal, setKanal] = useState<Kanal>("EMAIL");
  const mevcut = olay.sablonlar.find((s) => s.kanal === kanal) ?? null;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Buton gorunum="ikincil" onClick={onGeri}>
          ← Olay listesi
        </Buton>
        <h2 className="text-notr-900 text-base font-semibold">{olay.olay}</h2>
      </div>

      <Kart baslik="Kanal">
        <div className="flex flex-wrap gap-2">
          {DUZENLENEBILIR_KANALLAR.map((k) => {
            const varMi = olay.sablonlar.some((s) => s.kanal === k);
            return (
              <Buton
                key={k}
                gorunum={k === kanal ? "birincil" : "ikincil"}
                onClick={() => setKanal(k)}
              >
                {KANAL_ADI[k]}
                {varMi ? " ✓" : ""}
              </Buton>
            );
          })}
        </div>
        <KararBekleyenKanallar olay={olay} />
      </Kart>

      <SablonFormu
        key={`${olay.olay}-${kanal}`}
        olay={olay}
        kanal={kanal}
        mevcut={mevcut}
        arsivleyebilir={arsivleyebilir}
        hataYakala={hataYakala}
        onDegisti={onDegisti}
      />
    </>
  );
}

// =====================================================================
// FORM
// =====================================================================

function SablonFormu({
  olay,
  kanal,
  mevcut,
  arsivleyebilir,
  hataYakala,
  onDegisti,
}: {
  olay: SablonOlayi;
  kanal: Kanal;
  mevcut: SablonSatiri | null;
  arsivleyebilir: boolean;
  hataYakala: (tekrarDene: () => void) => (e: unknown) => void;
  onDegisti: () => void;
}) {
  const [baslik, setBaslik] = useState(mevcut?.baslik ?? "");
  const [govde, setGovde] = useState(mevcut?.govde ?? "");
  const [aktif, setAktif] = useState(mevcut?.aktif ?? true);
  const [kritik, setKritik] = useState(mevcut?.kritik ?? false);
  const [saglayiciAdi, setSaglayiciAdi] = useState(mevcut?.saglayiciAdi ?? "");
  const govdeRef = useRef<HTMLTextAreaElement>(null);

  const kaydet = useMutation({
    mutationFn: () =>
      sablonKaydet({
        olay: olay.olay,
        kanal,
        baslik: baslik.trim() || null,
        govde,
        aktif,
        kritik,
        saglayiciAdi: saglayiciAdi.trim() || null,
      }),
    onSuccess: onDegisti,
    onError: hataYakala(() => kaydet.mutate()),
  });

  const test = useMutation({
    mutationFn: () => sablonTestGonder(mevcut!.id),
    onError: hataYakala(() => test.mutate()),
  });

  const arsiv = useMutation({
    mutationFn: () => sablonArsivle(mevcut!.id),
    onSuccess: onDegisti,
    onError: hataYakala(() => arsiv.mutate()),
  });

  const onay = useMutation({
    mutationFn: () => sablonOnayaGonder(mevcut!.id),
    onSuccess: onDegisti,
    onError: hataYakala(() => onay.mutate()),
  });

  /**
   * ================================================================
   * ÖNİZLEME YEREL, DOĞRULAMA SUNUCUDA
   * ================================================================
   * Örnek değerler zaten sunucudan geldi; onlarla doldurmak için
   * ağa çıkmaya gerek yok, yazarken anında görünüyor.
   *
   * Ama "bu şablon geçerli mi" kararı BURADA verilmiyor: aynı kuralı
   * iki yerde yazmak, ikisinin ayrılacağı gün demektir ve o gün ekran
   * "geçerli" derken sunucu reddeder. Ekran yalnızca sunucunun
   * söylediğini gösteriyor.
   */
  const onizleme = useMemo(() => {
    let m = govde;
    for (const d of olay.degiskenler) {
      m = m.replaceAll(new RegExp(`\\{\\{\\s*${d.ad}\\s*\\}\\}`, "g"), d.ornek);
    }
    return m;
  }, [govde, olay.degiskenler]);

  const degiskenEkle = (ad: string) => {
    const el = govdeRef.current;
    const metin = `{{${ad}}}`;
    if (!el) {
      setGovde((g) => g + metin);
      return;
    }
    // İmlecin bulunduğu yere ekleniyor: sonuna eklemek, cümlenin
    // ortasına değişken koymak isteyen kişiyi elle kes-yapıştıra
    // zorlardı.
    const bas = el.selectionStart;
    const son = el.selectionEnd;
    setGovde((g) => g.slice(0, bas) + metin + g.slice(son));
  };

  return (
    <>
      <Kart
        baslik={`${KANAL_ADI[kanal]} şablonu`}
        aciklama={mevcut ? "Kayıtlı şablon" : "Henüz yok — yazıp kaydedin"}
        eylem={
          kanal === "WHATSAPP" && mevcut ? (
            <Rozet ton={ONAY_TONU[mevcut.onayDurumu]}>{ONAY_ADI[mevcut.onayDurumu]}</Rozet>
          ) : undefined
        }
      >
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            kaydet.mutate();
          }}
        >
          {kanal === "EMAIL" && (
            <Alan etiket="Konu" value={baslik} onChange={(e) => setBaslik(e.target.value)} />
          )}

          <MetinAlani
            etiket="Metin"
            ref={govdeRef}
            value={govde}
            onChange={(e) => setGovde(e.target.value)}
            rows={6}
            daktilo
            placeholder="Sayın {{kisiAdi}}, …"
          />

          <fieldset className="flex flex-col gap-2">
            <legend className="text-notr-700 text-sm">Kullanılabilir değişkenler</legend>
            <div className="flex flex-wrap gap-2">
              {olay.degiskenler.map((d) => (
                <button
                  key={d.ad}
                  type="button"
                  onClick={() => degiskenEkle(d.ad)}
                  /*
                    ================================================================
                    ACIKLAMA YALNIZCA `title`DEYDI — KLAVYEYE ULASMIYORDU
                    ================================================================
                    Dugmenin gorunen metni `{{kisiAdi}}`; degiskenin NE
                    OLDUGU ve ORNEGI yalnizca tooltip'teydi. Tooltip fare
                    ustune gelince cikiyor: klavyeyle sekmeleyen kullanici
                    hicbir zaman gormuyor, ekran okuyucular ise `title`i
                    tutarsiz okuyor (bazisi hic okumuyor).

                    `aria-label` gorunen metni ICERIYOR (WCAG 2.5.3
                    "Label in Name"): "{{kisiAdi}} — ..." seklinde
                    basliyor, yani sesle komut veren kullanici gordugu
                    metni soyleyerek dugmeyi bulabiliyor.

                    `title` FARE ICIN kaldi — kaldirmak fareyle gezen
                    kullanicidan bir seyi almak olurdu.
                  */
                  title={`${d.aciklama} — örnek: ${d.ornek}`}
                  aria-label={`{{${d.ad}}} — ${d.aciklama}. Örnek: ${d.ornek}`}
                  className="rounded-rozet border-notr-300 bg-notr-50 text-notr-800 hover:bg-notr-100 focus-visible:outline-marka-600 border px-2 py-1 font-mono text-xs transition-colors duration-(--sure-hizli) focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  {`{{${d.ad}}}`}
                </button>
              ))}
            </div>
            <p className="text-notr-600 text-xs">
              Listede olmayan bir değişken yazarsanız kayıt reddedilir — boş metin gönderilmesin
              diye.
            </p>
          </fieldset>

          {/*
  HAM `<input type="checkbox">` KALDIRILDI. Elle yazilan kutu
  `OnayKutusu`nun sozlesmesinin disinda kaliyordu: odak halkasi,
  `aria-describedby` bagi ve dokunma yuksekligi orada tek yerde
  tanimli. Ozellikle odak: klavyeyle gezen kullanici bu kutularda
  tarayicinin varsayilan halkasini goruyordu, panelin kendi
  `:focus-visible` bicimini degil.
*/}
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <OnayKutusu
              etiket="Aktif"
              checked={aktif}
              onChange={(e) => setAktif(e.target.checked)}
            />
            <OnayKutusu
              etiket="Sessiz saatte de gönder (22:00–08:00)"
              checked={kritik}
              onChange={(e) => setKritik(e.target.checked)}
            />
          </div>

          {kanal === "WHATSAPP" && (
            <Alan
              etiket="Sağlayıcıdaki şablon adı"
              value={saglayiciAdi}
              onChange={(e) => setSaglayiciAdi(e.target.value)}
              className="font-mono"
              placeholder="rapor_yayinlandi_tr"
            />
          )}

          {kaydet.isError && (
            <p className="text-hata-500 text-sm">
              {kaydet.error instanceof ApiHatasi ? kaydet.error.message : "Kaydedilemedi."}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Buton type="submit" disabled={kaydet.isPending || govde.trim().length === 0}>
              {kaydet.isPending ? "Kaydediliyor…" : "Kaydet"}
            </Buton>

            {mevcut && (
              <Buton gorunum="ikincil" type="button" onClick={() => test.mutate()}>
                {test.isPending ? "Gönderiliyor…" : "Kendime test gönder"}
              </Buton>
            )}

            {mevcut && kanal === "WHATSAPP" && (
              <Buton gorunum="ikincil" type="button" onClick={() => onay.mutate()}>
                Onaya gönder
              </Buton>
            )}

            {/* Arşivleme AYRI izin istiyor (matris §5.15/§5.16):
                koordinatör şablon yazar ama kapatamaz. */}
            {mevcut && arsivleyebilir && (
              <Buton gorunum="sessiz" type="button" onClick={() => arsiv.mutate()}>
                Arşivle
              </Buton>
            )}
          </div>

          {test.isSuccess && (
            <p className="text-onay-700 text-sm">Test bildirimi kendi hesabınıza gönderildi.</p>
          )}
          {test.isError && (
            <p className="text-hata-500 text-sm">
              {test.error instanceof ApiHatasi ? test.error.message : "Test gönderilemedi."}
            </p>
          )}
          {onay.isError && (
            <p className="text-hata-500 text-sm">
              {onay.error instanceof ApiHatasi ? onay.error.message : "Onaya gönderilemedi."}
            </p>
          )}
          {mevcut?.redNedeni && (
            <p className="text-hata-500 text-sm">Ret gerekçesi: {mevcut.redNedeni}</p>
          )}
        </form>
      </Kart>

      <Kart baslik="Önizleme" aciklama="Örnek değerlerle — gerçek öğrenci verisi kullanılmaz">
        {govde.trim().length === 0 ? (
          <BosDurum baslik="Metin yok" aciklama="Yukarıya bir şeyler yazın." />
        ) : (
          <p className="text-notr-800 text-sm whitespace-pre-wrap">{onizleme}</p>
        )}
      </Kart>
    </>
  );
}
